import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faShuffle } from '@fortawesome/free-solid-svg-icons'
import cx from 'classnames'
import React, { ChangeEvent, KeyboardEvent as ReactKeyboardEvent, useState } from 'react'

import { LEADERBOARD_TABS } from '../../../constants/constants'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import modalsSlice, { selectOpenLeaderboardInfo } from '../../../reducers/modalsSlice'
import {
  selectCurrentGamers,
  selectCurrentPlaylistSlug,
} from '../../../selectors/current-play-selectors'
import { selectMatchInfo } from '../../../selectors/match-selectors'
import { selectCurrentMatchSlug, selectScoreVersion } from '../../../selectors/session-selectors'
import { selectViewSettings } from '../../../selectors/settings-selectors'
import keyBindings from '../../../services/KeyBindings'
import trackMixer from '../../../services/TrackMixer'
import { onModalContainerRef } from '../../../util/scrolling'
import CloseIcon from '../../widgets/CloseIcon'
import { SelectWithLabel } from '../../widgets/TextInputWithLabel'
import ModalBackdrop from '../ModalBackdrop'
import StatsByTrackTable from './StatsByTrackTable'
import StatsByPlayerTable from './StatsByPlayerTable'
import TrackGallery from './TrackGallery'
import VisibilitySetting from './VisibilitySetting'

const LeaderboardModal = () => {
  const dispatch = useAppDispatch()
  const { initialTab } = useAppSelector(selectOpenLeaderboardInfo)
  const whichTab = LEADERBOARD_TABS[initialTab].key
  const scoreVersion = useAppSelector(selectScoreVersion)
  const { isShowLyrics } = useAppSelector(selectViewSettings(0))
  const currentMatchSlug = useAppSelector(selectCurrentMatchSlug)
  const [matchOwner, matchSlug] = currentMatchSlug.split('/')
  const currentPlaylistSlug = useAppSelector(selectCurrentPlaylistSlug)
  const {
    playlistOrder,
    playlists,
    title: matchTitle,
  } = useAppSelector(selectMatchInfo(matchOwner, matchSlug))
  const trackSetMenuItems = playlistOrder.map((playlistSlug) => {
    const { title } = playlists[playlistSlug]
    return { value: playlistSlug, name: title }
  })
  const [whichTrackSet, setWhichTrackSet] = useState(currentPlaylistSlug)
  const gamers = useAppSelector(selectCurrentGamers).filter(({ isActive }) => {
    return isActive
  })
  const [isShowTabs, setIsShowTabs] = useState(true)
  const [isSelectRandomTrackNow, setIsSelectRandomTrackNow] = useState(false)
  const onClose = () => {
    keyBindings.popModal()
    dispatch(modalsSlice.actions.toggleLeaderboardModal({ initialTab, isOpen: false }))
    // if (isWatching) {
    //   dispatch(toggleLeaderboard({ matchOwner, matchSlug, isWatch: false }))
    // }
  }
  const onSwitchTab = (tab: string) => {
    const tabIndex = LEADERBOARD_TABS.findIndex(({ key }) => key === tab)
    dispatch(modalsSlice.actions.toggleLeaderboardModal({ initialTab: tabIndex, isOpen: true }))
  }
  const onSwitchTrackset = (event: ChangeEvent<HTMLSelectElement>) => {
    setWhichTrackSet(event.target.value)
  }
  const onKeyDown = (event: ReactKeyboardEvent<HTMLSelectElement>) => {
    // handle up / down arrows only
    const step = event.keyCode === 40 ? 1 : event.keyCode === 38 ? -1 : 0
    if (step !== 0) {
      const currTarget = event.currentTarget
      const nextIndex = Math.min(
        Math.max(0, currTarget.selectedIndex + step),
        currTarget.options.length - 1
      )
      const nextValue = currTarget.options[nextIndex].value
      currTarget.selectedIndex = nextIndex
      currTarget.value = nextValue
      setWhichTrackSet(nextValue)
      event.stopPropagation()
    }
  }

  const getBody = () => {
    if (whichTab === 'admin') {
      return (
        <div className="nutherWrapper">
          <StatsByPlayerTable
            matchOwner={matchOwner}
            matchSlug={matchSlug}
            playlistSlug=""
            isPrintReady={!isShowTabs}
          />
        </div>
      )
    }
    if (whichTab === 'track') {
      return (
        <TrackGallery
          playlistSlug={whichTrackSet}
          scoreVersion={scoreVersion}
          isSelectRandomTrackNow={isSelectRandomTrackNow}
        />
      )
    }
    return (
      <div>
        {whichTab === 'round' && (
          <StatsByPlayerTable
            matchOwner={matchOwner}
            matchSlug={matchSlug}
            playlistSlug={whichTrackSet}
            isPrintReady={!isShowTabs}
          />
        )}
        {whichTab === 'match' && (
          <StatsByTrackTable matchSlug={currentMatchSlug} isPrintReady={!isShowTabs} />
        )}
      </div>
    )
  }
  const getRoundBanner = () => {
    const showBlasters = whichTab === 'track'
    const showBlaster = (blasterIndex: number) => {
      if (!isShowTabs || !showBlasters || blasterIndex >= gamers.length) {
        return null
      }
      const { playerName } = gamers[blasterIndex]
      const divClassname = cx('player', {
        left: blasterIndex === 0,
        right: blasterIndex === 1,
      })
      return (
        <div className={divClassname}>
          <div className="name">{playerName}</div>
        </div>
      )
    }
    return (
      <div className="round">
        {showBlaster(0)}
        <SelectWithLabel
          label=""
          itemArray={trackSetMenuItems}
          onChange={onSwitchTrackset}
          value={currentPlaylistSlug}
          onKeyDown={onKeyDown}
          // isDisableTabIndex={false && isPubMode}
        />
        {showBlaster(1)}
      </div>
    )
  }
  const getRight = () => {
    if (!isShowTabs) {
      return (
        <div className="logo">
          <div className="blastButton mini"></div>
        </div>
      )
    }
    const onSelectRandomTrack = () => {
      setIsSelectRandomTrackNow(true)
    }
    const onHideLyricsToggle = () => {
      trackMixer.toggleMasked(!isShowLyrics)
    }
    return (
      <>
        {whichTab === 'track' && (
          <div className="bonusButtons">
            <button onClick={onSelectRandomTrack}>
              <FontAwesomeIcon size="2xl" icon={faShuffle} title="Play Random Track!" />
            </button>
            <button onClick={onHideLyricsToggle}>
              <FontAwesomeIcon
                icon={isShowLyrics ? faEye : faEyeSlash}
                title={`${isShowLyrics ? 'Hide' : 'Show'} Lyrics`}
                size="2xl"
              />
            </button>
          </div>
        )}
        <VisibilitySetting />
      </>
    )
  }
  const getLeft = () => {
    if (isShowTabs) {
      return (
        <div className="mode-selector">
          {LEADERBOARD_TABS.map(({ name, key }, i) => (
            <button
              className={cx('pushButton', { active: key === whichTab })}
              key={key}
              onClick={() => onSwitchTab(key)}
            >
              {name}
            </button>
          ))}
        </div>
      )
    }
    return (
      <div className="logo">
        <span>Lyric Blaster</span>
        <div className="blastButton left mini"></div>
      </div>
    )
  }
  const toggleTabs = () => {
    setIsShowTabs(!isShowTabs)
  }
  const headingClassname = cx('modalHeading', { printReady: !isShowTabs })
  return (
    <ModalBackdrop isTop>
      <div className="modalContainer leaderboardModal" ref={onModalContainerRef} tabIndex={0}>
        <CloseIcon onClose={onClose} />
        <div className={headingClassname}>
          <div className="left">{getLeft()}</div>
          <div className="center">
            <div className="matchTitle" onClick={toggleTabs}>
              {matchTitle}
            </div>
            {(whichTab === 'round' || whichTab === 'track') && getRoundBanner()}
          </div>
          <div className="right">{getRight()}</div>
        </div>
        {getBody()}
      </div>
    </ModalBackdrop>
  )
}

export default LeaderboardModal
